import fetchFirstAmounts from "utils/orderAmounts";

const API = "https://trust-cargo.co/anas_backend/api/v2";

/**
 * How much the customer orders under each supplier's own code come to.
 *
 * A supplier IS a shop code - adding an order creates a supplier of the same
 * code - so the orders whose shop_code matches are that supplier's work:
 *
 *   Balance          SUM(final_amount) - everything paid to them
 *   Expected balance SUM(first_amount - the order's own deposit)
 *
 * The expected balance is the sum of each order's expected balance, which is
 * why the deposit is totalled per order here rather than taken from the
 * supplier's account. `deposit` on an order already carries both ledgers -
 * the customer's deposit and ours to the supplier - so there is nothing to
 * add to it here. A server new enough to send each order's own
 * `expected_balance` is simply totalled instead: that figure nets off the
 * payments to the supplier too, which this response cannot see.
 *
 * The server computes both and sends them with the supplier, in which case
 * this does nothing at all.
 *
 * Otherwise both are worked out here. `final_amount` comes off the orders
 * list, but `first_amount` is not on it, so the orders that actually belong
 * to a supplier are read individually to get it - only those, and only up to
 * DETAIL_CAP of them.
 */
export default async function withOrderTotals(suppliers) {
  if (!Array.isArray(suppliers) || suppliers.length === 0) return suppliers;

  // already provided - nothing to work out
  if (suppliers[0].orders_balance !== undefined) return suppliers;

  try {
    const res = await fetch(`${API}/orders?limit=2000`, { cache: "no-store" });
    if (!res.ok) return suppliers;

    const orders = await res.json();
    if (!Array.isArray(orders)) return suppliers;

    const codes = new Set(suppliers.map((s) => String(s.code || "").toUpperCase()).filter(Boolean));

    const mine = orders.filter((o) => codes.has(String(o.shop_code || "").toUpperCase()));

    // only the orders that actually belong to a supplier need this
    const firstById = await fetchFirstAmounts(mine);

    const totals = new Map();
    // whether first_amount could be established at all, per code
    const firstKnown = new Map();
    // the server's own per-order figure, when it sends one
    const expected = new Map();

    mine.forEach((o) => {
      const key = String(o.shop_code || "").toUpperCase();

      const t = totals.get(key) || { first: 0, final: 0, deposit: 0 };
      t.final += Number(o.final_amount ?? o.charged) || 0;

      // the inline value first: an order with no id is not in the map
      const first =
        o.first_amount !== undefined && o.first_amount !== null
          ? Number(o.first_amount)
          : firstById.get(o.id);

      if (first !== undefined && first !== null && !Number.isNaN(first)) {
        t.first += first;
        firstKnown.set(key, true);
      }

      /**
       * Only a quoted order's deposit is netted off, the same rule the
       * server follows. An order with no first_amount shows no expected
       * balance of its own, so taking its deposit off the total would make
       * the column smaller than the rows it is meant to add up.
       */
      if (Number(first) > 0) {
        t.deposit += Number(o.deposit) || 0;
      }

      /**
       * An order's expected balance nets off what we paid the supplier as
       * well, which is not in this response - so when the server sends the
       * figure per order, the total is the sum of those rather than a
       * second attempt at the formula. A null one is an unquoted order and
       * contributes nothing.
       */
      if (o.expected_balance !== undefined) {
        expected.set(key, (expected.get(key) || 0) + (Number(o.expected_balance) || 0));
      }

      totals.set(key, t);
    });

    return suppliers.map((s) => {
      const key = String(s.code || "").toUpperCase();
      const t = totals.get(key) || { first: 0, final: 0, deposit: 0 };
      const paid = Number(s.paid) || 0;

      // a supplier with no orders has an expected balance too - it is simply
      // what has been paid out against nothing
      const known = firstKnown.get(key) || !totals.has(key);

      return {
        ...s,
        orders_first: t.first,
        orders_final: t.final,
        orders_balance: Math.round((t.final - paid) * 100) / 100,
        /**
         * The server's own per-order figures when they are there, and the
         * quote less the deposit when they are not.
         *
         * null, not 0, when first_amount could not be established either
         * way: zero would read as "nothing expected" rather than "not
         * known".
         */
        // eslint-disable-next-line no-nested-ternary
        orders_expected_balance: expected.has(key)
          ? Math.round(expected.get(key) * 100) / 100
          : known
          ? Math.round((t.first - t.deposit) * 100) / 100
          : null,
      };
    });
  } catch (e) {
    return suppliers;
  }
}
